import { getArrayKeysFromObject, isDefined, isNumber, numberOrZero } from "./utils";

const processSimpleValue = (value: any) => {
  if (isNumber(value)) {
    return numberOrZero(value);
  } else if (value === "true" || value === "false") {
    return value === "true";
  } else {
    return value;
  }
};

class URLQueryImpl {
  public copy(searchParams: URLSearchParams) {
    return new URLSearchParams(searchParams.toString());
  }

  public toObject(searchParams: URLSearchParams): Record<string, string> {
    return Object.fromEntries(searchParams.entries());
  }

  public merge(target: URLSearchParams, source: URLSearchParams) {
    const copy = this.copy(target);

    source.forEach((value, key) => {
      copy.append(key, value);
    });

    return copy;
  }

  public searchParamsToString({
    searchParams,
    append,
  }: {
    searchParams: Record<string, string | number | boolean | undefined | string[] | number[]>;
    append?: boolean;
  }) {
    const resultParams = new URLSearchParams(append ? window.location.search : undefined);

    Object.entries(searchParams).forEach(([searchParamKey, searchParamValue]) => {
      if (!isDefined(searchParamValue) || resultParams.has(searchParamKey)) {
        resultParams.delete(searchParamKey);
      }

      if (Array.isArray(searchParamValue)) {
        searchParamValue.forEach((value) => {
          resultParams.append(searchParamKey, value.toString());
        });
      } else if (isDefined(searchParamValue) && searchParamValue !== "") {
        resultParams.append(searchParamKey, searchParamValue.toString());
      }
    });

    const prefix = [...resultParams.keys()].length ? "?" : "";
    return `${prefix}${resultParams.toString()}`;
  }

  public getGenericQueryParams = <T>(
    searchParams: URLSearchParams,
    paramKeys: Array<keyof T>,
    defaultQueryParamsObject?: T,
  ) => {
    let result: Partial<T> = {};

    const arrayKeys = defaultQueryParamsObject ? getArrayKeysFromObject(defaultQueryParamsObject) : [];

    paramKeys.forEach((param) => {
      const paramString = param.toString();

      if (!searchParams.has(paramString)) {
        return;
      }

      const value = searchParams.getAll(paramString);

      if (value.length > 1 || arrayKeys.includes(param)) {
        result = { ...result, [param]: this.getQueryArray(value) };
      } else {
        result = { ...result, [param]: processSimpleValue(value[0]) };
      }
    });

    return result;
  };

  public getQueryArray<T>(arrayQueryParamData?: T[] | T): T[] | undefined {
    if (!arrayQueryParamData) {
      return;
    }

    if (Array.isArray(arrayQueryParamData)) {
      return arrayQueryParamData.map(processSimpleValue);
    }

    return [processSimpleValue(arrayQueryParamData)];
  }

  public getTableChangePageIndex(pageIndex: number) {
    return this.searchParamsToString({
      searchParams: {
        pageIndex: pageIndex > 1 ? pageIndex - 1 : undefined,
      },
      append: true,
    });
  }
}

export const URLQuery = new URLQueryImpl();
